import React, { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Home, LayoutGrid, Compass, Crown, Sparkles, BookOpen, Hash, ArrowRight } from "lucide-react";
import { getMyMoodcards } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { track } from "../lib/track";

/**
 * CommandPalette — desktop ⌘K / Ctrl+K spotlight.
 *
 * Single keystroke opens a sheet with:
 *   • Quick-jump nav items (home, dashboard, explore, creators, premium, sign-in)
 *   • Recent moodcards (last 8 the user has authored or viewed)
 *   • Fuzzy filter as you type
 *
 * Closed on Esc, on result-click, or outside-click. Mounts once at the
 * App root. Mobile users (lg breakpoint and below) never see it.
 */
function fuzzyScore(query, target) {
    if (!query) return 1;
    const q = query.toLowerCase();
    const t = (target || "").toLowerCase();
    if (t.includes(q)) return 2;
    // Light fuzzy — count adjacent character matches.
    let i = 0;
    let hits = 0;
    for (const ch of q) {
        const idx = t.indexOf(ch, i);
        if (idx === -1) return 0;
        hits += 1;
        i = idx + 1;
    }
    return hits / q.length;
}

export default function CommandPalette() {
    const navigate = useNavigate();
    const { user, isPremium } = useAuth();
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [recent, setRecent] = useState([]);
    const [activeIdx, setActiveIdx] = useState(0);
    const inputRef = useRef(null);

    // ⌘K / Ctrl+K + Esc handlers.
    useEffect(() => {
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                setOpen((v) => !v);
                track("cmdk_toggled");
            } else if (e.key === "Escape" && open) {
                setOpen(false);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open]);

    // Focus input on open + load recent cards.
    useEffect(() => {
        if (!open) {
            setQuery("");
            setActiveIdx(0);
            return;
        }
        setTimeout(() => inputRef.current?.focus(), 50);
        if (user) {
            getMyMoodcards().then((data) => setRecent((data?.items || data || []).slice(0, 8))).catch(() => {});
        }
    }, [open, user]);

    const navItems = useMemo(() => {
        const items = [
            { id: "home", label: "Home — make a card", icon: Home, action: () => navigate("/") },
            { id: "dashboard", label: "Dashboard — your stats + backdrop", icon: LayoutGrid, action: () => navigate("/dashboard") },
            { id: "explore", label: "Explore — the public gallery", icon: Compass, action: () => navigate("/explore") },
            { id: "my-vibe", label: "My Vibe — tune cards to sound like you", icon: Sparkles, action: () => navigate("/my-vibe") },
            { id: "creators", label: "Creators — Premium signature gallery", icon: Crown, action: () => navigate("/creators") },
            { id: "history", label: "History — your card timeline", icon: BookOpen, action: () => navigate("/history") },
        ];
        if (!isPremium) {
            items.push({ id: "premium", label: "Premium — unlock everything", icon: Sparkles, action: () => navigate("/?premium=1") });
        }
        if (!user) {
            items.push({ id: "signin", label: "Sign in", icon: Hash, action: () => navigate("/?signin=1") });
        }
        return items;
    }, [navigate, user, isPremium]);

    const filteredNav = useMemo(
        () => navItems.map((i) => ({ ...i, _score: fuzzyScore(query, i.label) })).filter((i) => i._score > 0).sort((a, b) => b._score - a._score),
        [navItems, query]
    );
    const filteredCards = useMemo(
        () => recent
            .map((c) => ({ ...c, _score: fuzzyScore(query, c.feeling) }))
            .filter((c) => c._score > 0)
            .sort((a, b) => b._score - a._score)
            .slice(0, 6),
        [recent, query]
    );

    const total = filteredNav.length + filteredCards.length;

    const runItem = (item) => {
        if (!item) return;
        setOpen(false);
        track("cmdk_action", { id: item.id || `card-${item.id}` });
        item.action();
    };

    const handleKey = (e) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIdx((i) => Math.min(i + 1, total - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIdx((i) => Math.max(i - 1, 0));
        } else if (e.key === "Enter") {
            e.preventDefault();
            if (activeIdx < filteredNav.length) {
                runItem(filteredNav[activeIdx]);
            } else {
                const c = filteredCards[activeIdx - filteredNav.length];
                if (c) {
                    setOpen(false);
                    track("cmdk_card_opened", { id: c.id });
                    navigate(`/results/${c.id}`);
                }
            }
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="fixed inset-0 z-[80] bg-black/55 backdrop-blur-sm flex items-start justify-center pt-[15vh] px-4"
                    data-testid="cmdk-overlay"
                    onClick={() => setOpen(false)}
                >
                    <motion.div
                        initial={{ opacity: 0, y: -8, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -8, scale: 0.98 }}
                        transition={{ duration: 0.18 }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full max-w-xl rounded-2xl bg-surface-1 border border-app/20 shadow-2xl overflow-hidden"
                        data-testid="cmdk-panel"
                    >
                        <div className="flex items-center gap-3 px-4 py-3 border-b border-app/15">
                            <Search className="w-4 h-4 text-app-3" strokeWidth={1.5} />
                            <input
                                ref={inputRef}
                                type="text"
                                value={query}
                                onChange={(e) => { setQuery(e.target.value); setActiveIdx(0); }}
                                onKeyDown={handleKey}
                                placeholder="jump anywhere · search your cards"
                                data-testid="cmdk-input"
                                className="flex-1 bg-transparent outline-none font-sans text-sm text-app placeholder:text-app-3"
                            />
                            <kbd className="font-sans text-[9px] uppercase tracking-[0.25em] text-app-3 px-2 py-1 rounded-md border border-app/20">esc</kbd>
                        </div>

                        <div className="max-h-[60vh] overflow-y-auto">
                            {filteredNav.length > 0 && (
                                <div className="px-2 py-2">
                                    <div className="px-3 pb-1 font-sans text-[9px] uppercase tracking-[0.32em] text-app-3">jump to</div>
                                    {filteredNav.map((it, i) => {
                                        const Icon = it.icon;
                                        const isActive = activeIdx === i;
                                        return (
                                            <button
                                                key={it.id}
                                                type="button"
                                                onClick={() => runItem(it)}
                                                onMouseEnter={() => setActiveIdx(i)}
                                                data-testid={`cmdk-nav-${it.id}`}
                                                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                                                    isActive ? "bg-amber-400/10 text-app" : "text-app-2 hover:text-app"
                                                }`}
                                            >
                                                <Icon className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={1.5} />
                                                <span className="font-sans text-xs flex-1">{it.label}</span>
                                                {isActive && <ArrowRight className="w-3 h-3 text-amber-200/85" strokeWidth={1.5} />}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}

                            {filteredCards.length > 0 && (
                                <div className="px-2 py-2 border-t border-app/10">
                                    <div className="px-3 pb-1 font-sans text-[9px] uppercase tracking-[0.32em] text-app-3">your recent cards</div>
                                    {filteredCards.map((c, i) => {
                                        const idx = filteredNav.length + i;
                                        const isActive = activeIdx === idx;
                                        return (
                                            <button
                                                key={c.id}
                                                type="button"
                                                onClick={() => { setOpen(false); navigate(`/results/${c.id}`); }}
                                                onMouseEnter={() => setActiveIdx(idx)}
                                                data-testid={`cmdk-card-${c.id}`}
                                                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                                                    isActive ? "bg-amber-400/10 text-app" : "text-app-2 hover:text-app"
                                                }`}
                                            >
                                                <span className="w-1.5 h-1.5 rounded-full bg-amber-200/65 flex-shrink-0" />
                                                <span className="font-serif italic text-xs flex-1 truncate">
                                                    {c.feeling || "(untitled)"}
                                                </span>
                                                {isActive && <ArrowRight className="w-3 h-3 text-amber-200/85" strokeWidth={1.5} />}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}

                            {total === 0 && (
                                <div className="px-5 py-10 text-center" data-testid="cmdk-empty">
                                    <p className="font-serif italic text-app-3 text-sm">nothing matches "{query}"</p>
                                </div>
                            )}
                        </div>

                        <div className="px-4 py-2.5 border-t border-app/10 bg-app/[0.015] flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <span className="font-sans text-[9px] uppercase tracking-[0.28em] text-app-3 flex items-center gap-1.5">
                                    <kbd className="px-1.5 py-0.5 rounded border border-app/20">↑</kbd>
                                    <kbd className="px-1.5 py-0.5 rounded border border-app/20">↓</kbd>
                                    navigate
                                </span>
                                <span className="font-sans text-[9px] uppercase tracking-[0.28em] text-app-3 flex items-center gap-1.5">
                                    <kbd className="px-1.5 py-0.5 rounded border border-app/20">enter</kbd>
                                    open
                                </span>
                            </div>
                            <span className="font-sans text-[9px] uppercase tracking-[0.28em] text-app-3">moodcard · ⌘K</span>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
